import type {IKarelState} from '@cip/code-runner';
import type {LoadedAssignmentData} from 'assignments/models/loadedAssignmentData';
import type {FreeFlowParams} from 'mathide/math-ide-agent/freeFlow';
import type {ProjectFileStructure, ProjectId} from 'projects/types';
import type {CourseId, UserId} from 'types/common';
import type {ProjectFileId} from 'projects/types';
import {
  collection,
  doc,
  type CollectionReference,
  type DocumentReference,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import {firestore} from 'firebaseApp';

/**
 * Author identity snapshot stored on the published doc at publish time
 * (see `publishProject` in ide/utils/general and
 * `publishMathProject` in mathide/publishMathProject).
 */
export interface PublishedUserInfo {
  /** Display name shown as "Created by {displayName}" on the share page. */
  displayName: string;
  /** The author's user id — used for the owner check and profile links. */
  id: UserId;
  /** Avatar URL; empty string when the author has no photo. */
  photoURL: string;
}

/** Fields common to every published project, regardless of source IDE. */
interface PublishedProjectDataBase {
  date: Date;
  user: PublishedUserInfo;
  editors: UserId[];
  title: string;
  numLikes?: number;
  numRuns?: number;
  /** Owner-editable share-page message (see ProjectDescription in SharePageChrome.tsx). */
  note?: string;
}

/**
 * Published Python/Karel/Graphics project — the original share-page shape.
 *
 * `projectType` is a type-level discriminant only: no writer ever sets it on
 * Firestore (the field is absent on every existing regular doc, same as
 * `title` was for early publishes — see the comment in `PublishedWithData`).
 * Declaring it as a required literal, rather than `projectType?: undefined`,
 * is required for the union to narrow correctly — this project's tsconfig
 * runs with `strict: false` (`strictNullChecks: false`), under which
 * TypeScript's discriminated-union narrowing does not reliably exclude a
 * member via an `undefined`-typed discriminant. Never branch on
 * `projectType === 'regular'` at runtime; branch on
 * `projectType !== 'mathReact'` instead (see `Published.tsx`).
 */
export interface RegularPublishedProjectData extends PublishedProjectDataBase {
  projectType: 'regular';
  files: ProjectFileStructure;
  mainFile: ProjectFileId;
  assnData: LoadedAssignmentData;
  showTerminal: boolean;
  karelWorld: IKarelState;
  /** Whether to skip the karel compiler. Defaults to false. */
  skipKarelCompiler?: boolean;
}

/**
 * Published Math IDE React project. Written by
 * {@link import('../mathide/publishMathProject').publishMathProject} to the
 * same `published/{courseId}/studentPublished/{projectId}` document as
 * {@link RegularPublishedProjectData}, discriminated by `projectType`. The
 * share page renders this via `MathPublished` (sandboxed iframe) rather than
 * the Pyodide/Karel runner — see
 * [#3522](https://github.com/CodeinPlace/cip/issues/3522).
 */
export interface MathPublishedProjectData extends PublishedProjectDataBase {
  projectType: 'mathReact';
  /** The assignment's React app shell, keyed by project-relative path. */
  reactFiles: Record<string, string>;
  /** The student's Python source at publish time, run via the iframe's proxy. */
  pythonCode: string;
  /** Source assignment id — resolves DATASET_REQUEST messages on the share page. */
  assignmentId: string;
  /** Course id — resolves the assignment's datasets doc on the share page. */
  courseId: CourseId;
}

/**
 * Published `ai-text` agent-choice project. Written by
 * {@link import('../mathide/math-ide-agent/publishMathIdeAgentProject').publishMathIdeAgentProject}
 * to the same `published/{courseId}/studentPublished/{projectId}` document as
 * {@link RegularPublishedProjectData} and {@link MathPublishedProjectData},
 * discriminated by `projectType`. Unlike `mathReact`, this holds no
 * executable student code — `freeFlowParams` is a snapshot of the derived
 * "Your App" state, so the share page re-renders it directly with
 * `FreeFlowSolutionPanel` (no sandboxed iframe needed). See
 * [#3792](https://github.com/CodeinPlace/cip/issues/3792).
 */
export interface MathIdeAgentPublishedProjectData extends PublishedProjectDataBase {
  projectType: 'mathAgentChoice';
  /** Snapshot of the free-flow/beats parameter bag driving the Your App pane at publish time. */
  freeFlowParams: FreeFlowParams;
  /** Source assignment id (`ai-text`). */
  assignmentId: string;
  /** Course id. */
  courseId: CourseId;
}

export type PublishedProjectData =
  | RegularPublishedProjectData
  | MathPublishedProjectData
  | MathIdeAgentPublishedProjectData;

const publishedProjectDataConverter: FirestoreDataConverter<PublishedProjectData> =
  {
    toFirestore: (project: PublishedProjectData) => {
      return project;
    },
    fromFirestore: (snapshot: QueryDocumentSnapshot<PublishedProjectData>) => {
      return snapshot.data() as PublishedProjectData;
    },
  };

export const getPublishedProjectDataCollectionRef = (
  courseId: CourseId,
): CollectionReference<PublishedProjectData> => {
  return collection(
    firestore,
    `published/${courseId}/studentPublished`,
  ).withConverter(publishedProjectDataConverter);
};

export const getPublishedProjectDataDocumentRef = (
  courseId: CourseId,
  projectId: ProjectId,
): DocumentReference<PublishedProjectData> => {
  return doc(
    firestore,
    `published/${courseId}/studentPublished/${projectId}`,
  ).withConverter(publishedProjectDataConverter);
};
